import { DownloadOutlined, ListAltOutlined } from "@mui/icons-material";
import PsychologyAltOutlinedIcon from "@mui/icons-material/PsychologyAltOutlined";
import { LoadingButton } from "@mui/lab";
import { Chip, Grid, Paper, Tooltip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useMemo } from "react";

export const TEAM_EXPORT_COLUMNS = [
  {
    field: "candidat_name",
    headerName: "Nom et prénoms de l'employé",
    width: 240,
  },
  { field: "function_name", headerName: "Fonction", width: 220 },
  { field: "service_code", headerName: "Service", width: 110 },
  { field: "section_1", headerName: "Note section 1", width: 130 },
  { field: "section_2", headerName: "Note section 2", width: 130 },
  // { field: "section_3", headerName: "Note section 3", width: 130 },
  // { field: "section_4", headerName: "Note section 4", width: 130 },
  { field: "NOTE_GENERALE", headerName: "Note générale", width: 150 },
  { field: "appreciation", headerName: "Appréciation", width: 160 },
  { field: "dernier_evaluateur", headerName: "Dernier évaluateur", width: 220 },
  { field: "sup_f_name", headerName: "Supérieur hiérarchique", width: 230 },
  { field: "superieur_fonction", headerName: "Supérieur fonction", width: 180 },
  // { field: "evaluee", headerName: "Évalué", width: 110 },
  { field: "evaluation_status", headerName: "Statut", width: 130 },
  { field: "evaluation_date", headerName: "Date d'évaluation", width: 180 },
  { field: "actions", headerName: "Actions", width: 350 },
];

export default function MesEquipesTable({
  rows,
  total,
  loading,
  pagination,
  onPaginationChange,
  onEvaluate,
  onDownload,
  onValidate,
}) {
  const gridColumns = useMemo(
    () =>
      TEAM_EXPORT_COLUMNS.map((column) => ({
        ...column,
        sortable: false,
        renderCell: ({ row, value }) => {
          if (column.field === "actions")
            return (
              <Grid container spacing={1} wrap="nowrap">
                <Grid item>
                  <Tooltip title={Number(row.can_evaluate) === 1
                    ? "Cliquer pour commencer l'évaluation"
                    : "Le niveau hiérarchique précédent doit terminer son évaluation avant que vous puissiez évaluer cette personne."}>
                    <span>
                      <LoadingButton
                        onClick={() => onEvaluate(row)}
                        variant="outlined"
                        size="small"
                        disabled={Number(row.can_evaluate) !== 1}
                        startIcon={
                          row.evaluee_par_moi === "Oui" ? (
                            <ListAltOutlined />
                          ) : (
                            <PsychologyAltOutlinedIcon />
                          )
                        }
                      >
                        {row.evaluee_par_moi === "Oui" ? "Afficher" : "Évaluer"}
                      </LoadingButton>
                    </span>
                  </Tooltip>
                </Grid>
                <Grid item>
                  <LoadingButton
                    onClick={() => onDownload(row)}
                    color="primary"
                    variant="outlined"
                    size="small"
                    disabled={
                      row.ID_REPONSE == null || Number(row.TERMINEE) !== 1
                    }
                    startIcon={<DownloadOutlined />}
                  >
                    Fiche
                  </LoadingButton>
                </Grid>
                {/* <Grid item>
                  <LoadingButton
                    onClick={() => onValidate(row)}
                    color="primary"
                    variant="outlined"
                    size="small"
                    disabled={
                      row.ID_REPONSE == null || Number(row.TERMINEE) !== 1
                    }
                    startIcon={<CheckCircleOutlineOutlined />}
                  >
                    Valider
                  </LoadingButton>
                </Grid> */}
              </Grid>
            );
          if (column.field === "evaluation_status")
            return (
              <Chip
                size="small"
                label={value ?? "Non évalué"}
                color={
                  value === "En cours"
                    ? "warning"
                    : value === "Envoyée"
                      ? "success"
                      : "default"
                }
                variant="outlined"
              />
            );
          if (column.field === "evaluee")
            return (
              <Chip
                size="small"
                label={value}
                color={value === "Oui" ? "success" : "warning"}
                variant="outlined"
              />
            );
          return (
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                lineHeight: 1.4,
              }}
            >
              {value ?? "N/A"}
            </Typography>
          );
        },
      })),
    [onEvaluate, onDownload, onValidate],
  );
  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        border: "1px solid #e2e8f0",
        overflow: "hidden",
      }}
    >
      <DataGrid
        rows={rows}
        columns={gridColumns}
        getRowId={(row) => row.user_id}
        autoHeight
        getRowHeight={() => "auto"}
        estimatedRowHeight={68}
        pageSizeOptions={[5, 10, 25, 50]}
        loading={loading}
        rowCount={total}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={onPaginationChange}
        disableRowSelectionOnClick
        localeText={{ noRowsLabel: "Aucun employé à afficher" }}
        sx={{
          minWidth: 0,
          width: "100%",
          border: 0,
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#e3f2fd",
            color: "#0d47a1",
            borderBottom: "2px solid #90caf9",
          },
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#e3f2fd",
            borderRight: "1px solid #bbdefb",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontSize: 13,
            fontWeight: 700,
            whiteSpace: "normal",
            lineHeight: 1.25,
          },
          "& .MuiDataGrid-cell": {
            fontSize: 13,
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            borderRight: "1px solid #e2e8f0",
            borderBottom: "1px solid #eef2f7",
            py: 1,
            lineHeight: 1.4,
          },
          "& .MuiDataGrid-row:nth-of-type(odd)": {
            backgroundColor: "#f8fafc",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#eef6ff",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "1px solid #e2e8f0",
            bgcolor: "#fff",
          },
        }}
      />
    </Paper>
  );
}
